import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api.config';

const decodeJwtPayload = (token) => {
  try {
    const payload = token.split('.')[1];

    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      '='
    );

    return JSON.parse(atob(paddedPayload));
  } catch (error) {
    return null;
  }
};

const isJwtExpired = (token) => {
  const payload = decodeJwtPayload(token);

  if (!payload || typeof payload.exp !== 'number') {
    return true;
  }

  return Date.now() >= payload.exp * 1000;
};

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */
class AuthService {
  clearSession() {
    apiClient.removeToken();
    localStorage.removeItem('user');
  }

  /**
   * User signup/registration
   * @param {Object} userData - User registration data
   * @returns {Promise} API response
   */
  async signup(userData) {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.SIGNUP, userData);
    return response;
  }

  /**
   * User login
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} API response with tokens
   */
  async login(email, password , role = "researcher") {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, {
      email,
      password,
      role,
    });

    if (response.status === 'success' && response.data.tokens) {
      // Store tokens
      apiClient.setToken(response.data.tokens.access);
      localStorage.setItem('refresh_token', response.data.tokens.refresh);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response;
  }

  /**
   * User logout
   * @returns {Promise} API response
   */
  async logout() {
    const refreshToken = localStorage.getItem('refresh_token');
    const headers = apiClient.getHeaders();

    this.clearSession();

    if (refreshToken) {
      apiClient.request(
        API_ENDPOINTS.AUTH.LOGOUT,
        {
          method: 'POST',
          body: JSON.stringify({ refresh_token: refreshToken }),
          headers,
          keepalive: true,
        },
        false
      ).catch((error) => {
        console.error('Background logout failed:', error);
      });
    }

    return { status: 'success' };
  }

  /**
   * Get user profile
   * @returns {Promise} User profile data
   */
  async getProfile() {
    const response = await apiClient.get(API_ENDPOINTS.AUTH.PROFILE);
    if (response.status === 'success' && response.data) {
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    return response;
  }

  /**
   * Update user profile
   * @param {Object} profileData - Updated profile data
   * @returns {Promise} API response
   */
  async updateProfile(profileData) {
    const response = await apiClient.put(API_ENDPOINTS.AUTH.PROFILE, profileData);
    if (response.status === 'success' && response.data) {
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    return response;
  }

  /**
   * Change password
   * @param {Object} passwordData - Old and new password data
   * @returns {Promise} API response
   */
  async changePassword(passwordData) {
    const response = await apiClient.post(
      API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
      passwordData
    );
    return response;
  }

  /**
   * Forgot password - request reset
   * @param {string} email - User email
   * @returns {Promise} API response
   */
  async forgotPassword(email) {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      email,
    });
    return response;
  }

  /**
   * Reset password with token
   * @param {string} token - Reset token from email
   * @param {string} newPassword - New password
   * @param {string} confirmPassword - Confirm new password
   * @returns {Promise} API response
   */
  async resetPassword(token, newPassword, confirmPassword) {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      token,
      new_password: newPassword,
      new_password_confirm: confirmPassword,
    });
    return response;
  }

  /**
   * Verify email with token
   * @param {string} token - Verification token from email
   * @returns {Promise} API response
   */
  async verifyEmail(token) {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.VERIFY_EMAIL, {
      token,
    });
    return response;
  }

  /**
   * Resend verification email
   * @param {string} email - User email
   * @returns {Promise} API response
   */
  async resendVerification(email) {
    const response = await apiClient.post(
      API_ENDPOINTS.AUTH.RESEND_VERIFICATION,
      { email }
    );
    return response;
  }

  /**
   * Refresh access token
   * @returns {Promise} API response with new access token
   */
  async refreshToken() {
    const refreshToken = localStorage.getItem('refresh_token');
    const response = await apiClient.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
      refresh: refreshToken,
    });

    if (response.access) {
      apiClient.setToken(response.access);
    }

    return response;
  }

  /**
   * Validate token
   * @returns {Promise} API response
   */
  async validateToken() {
    const response = await apiClient.post(API_ENDPOINTS.AUTH.VALIDATE_TOKEN, {
      token: apiClient.getToken(),
    });
    return response;
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    const token = apiClient.getToken();

    if (!token) {
      return false;
    }

    if (isJwtExpired(token)) {
      this.clearSession();
      return false;
    }

    return true;
  }

  /**
   * Get current user from localStorage
   * @returns {Object|null} User object or null
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        return null;
      }
    }
    return null;
  }
}

const authService = new AuthService();

export default authService;
